/**
 * All functionality needed for Iframe components.
 *
 * @module feature/Iframe
 */
import lodash from 'lodash';
import QueryStringHandler from 'QueryStringHandler';

/**
 * Iframe functionality.
 *
 * @requires lodash
 * @requires module:project/Common.QueryStringHandler
 * @memberof module:feature/Iframe
 * @version 1.0.0
 * @author Rocco Janse <rocco.janse@valtech.nl>
 */
class Iframe {

    /**
     * Upgrades DOM element initializes window postmessage functionality.
     * @param {DOMElement} element DOM element to upgrade.
     */
    constructor(element) {

        this.$element = $(element);

        // handle current domain
        let parts = document.domain.split('.');

        while (parts.length > 2) {
            parts.shift();
        }

        document.domain = parts.join('.');

        this.targetOrigin = '*';
        this.oldIframeScrollTopPosition = 0;

        // passtrough querystring vars
        this.passThrough = (this.$element.attr('data-passthrough') === 'true') ? true : false;
        this.frameUrl = this.$element.attr('src');
        this.qsParams = [];

    }

    /**
     * Initializes Iframe and listeners.
     */
    init() {

        // passthrough params of querystring
        if (this.passThrough) {
            this.qsParams = QueryStringHandler.getParams();
            this.currentParams = QueryStringHandler.getParams(QueryStringHandler.getQueryStringFromUrl(this.frameUrl, true));
            const newParams = QueryStringHandler.updateParams(this.qsParams, this.currentParams);
            const newUrl = `${this.frameUrl.split('?')[0]}?${newParams}`;
            this.$element.attr('src', newUrl);
        }

        window.addEventListener('message', (event) => {

            if (event.data['function']) {
                let height = 0;

                switch (event.data['function']) {

                    case 'scrollToIframeTop':
                        this.scrollToIframeTop(event.data['arguments'][0]);
                        break;

                    case 'resizeFrame':
                        this.resizeFrame();
                        break;

                    case 'pageContentScrollHeight':
                        height = event.data['arguments'][0];
                        this.responseItCallback(height);
                        break;

                    case 'getWindowSize':
                        var msg = {
                            function: 'setParentWindowSize',
                            arguments: [document.documentElement.clientWidth, document.documentElement.clientHeight]
                        };
                        event.source.postMessage(msg, this.targetOrigin);
                        break;

                }
            }
        });

        // window resize
        $(window).on('resize.iframe', lodash.debounce(this.responseIt.bind(this), 200));

    }

    findPo(obj) {
        let curtop = 0;
        if (obj) {
            if (obj.offsetParent) {
                do {
                    curtop += obj.offsetTop;
                }
                while (obj === obj.offsetParent);
                return [curtop];
            }
        }
    }

    resizeFrame() {
        this.responseIt();
    }

    scrollToIframeTop(menuHeight) {
        const frame = this.$element[0];
        window.scroll(0,parseInt(this.findPos(frame))+parseInt(menuHeight));
    }

    responseItCallback(height) {
        const frame = this.$element[0];
        if (frame && height) {
            height = height + 20;
            frame.height = height;
            frame.style.height = (height) + 'px';
        }
    }

    responseIt() {
        const frame = this.$element[0];
        if (frame) {
            const msg = {
                function: 'getPageContentScrollHeight',
                arguments: []
            };
            frame.contentWindow.postMessage(msg, this.targetOrigin);
        }
    }

    /**
     * Saves current scroll position.
     */
    saveScrollPosition() {
        this.oldIframeScrollTopPosition = (window.pageYOffset !== undefined) ? window.pageYOffset : (document.documentElement || document.body.parentNode || document.body).scrollTop;
    }

    /**
     * Restores last scroll position.
     */
    restoreScrollPosition() {
        window.scroll(0, this.oldIframeScrollTopPosition);
    }
}

// register to Component Handler
window.ComponentHandler.register({
    constructor: Iframe,
    classAsString: 'Iframe',
    cssClass: 'js-iframe'
});

export default Iframe;